<!-- components/ComboboxControl.vue -->
<script setup lang="ts">
import { computed, ref, toRefs, watch, onMounted } from 'vue'
import { VCombobox, VIcon } from 'vuetify/components'
import FieldControl from './FieldControl.vue'
import InputShell from './InputShell.vue'
import searchIcon from '@/assets/icons/grey/search.svg'
import type { Item } from '@/types/corporative'

type ValidateOn = 'input' | 'blur' | 'submit'

const props = withDefaults(
  defineProps<{
    /** Vee-Validate field name (obrigatório) */
    name: string

    /** Rótulo visual (fica dentro do campo) */
    label?: string
    required?: boolean
    hint?: string

    /** v-model externo */
    modelValue?: string | Item | Item[] | null

    /** Estratégia de validação */
    validateOn?: ValidateOn

    /** Fonte de dados */
    items: Item[] | null

    /** Mapeamentos */
    labelField?: keyof Item | string
    valueField?: keyof Item | string

    /** UX */
    placeholder?: string
    multiple?: boolean
    disabled?: boolean
    loading?: boolean
    error?: boolean
    clearable?: boolean
    id?: string

    /** Busca incremental */
    searchable?: boolean
    /** Debounce (ms) p/ emitir 'search' */
    searchDebounceMs?: number

    /** Chips quando múltiplo */
    useChipsWhenMultiple?: boolean

    /** Fecha o menu ao selecionar (single) */
    closeOnSelect?: boolean

    /** Mantém valores não listados em items */
    allowCustom?: boolean
  }>(),
  {
    label: '',
    required: false,
    hint: '',
    modelValue: null,
    validateOn: 'input',
    items: () => [],
    labelField: 'name',
    valueField: 'id',
    placeholder: '',
    multiple: false,
    disabled: false,
    loading: false,
    error: false,
    clearable: true,
    id: '',
    searchable: true,
    searchDebounceMs: 250,
    useChipsWhenMultiple: true,
    closeOnSelect: true,
    allowCustom: false,
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | Item | Item[] | null): void
  (e: 'select', value: Item | Item[] | null): void
  (e: 'submit'): void
  (e: 'switch-modal'): void
  (e: 'search', value: string): void
}>()

const { items, labelField, valueField, multiple, searchable } = toRefs(props)

const comboboxRef = ref<InstanceType<typeof VCombobox> | null>(null)
const searchText = ref('')
const menuOpen = ref(false) // controla abertura do menu publicamente (sem acessar internals)

/** chips apenas quando múltiplo + flag ligada */
const chips = computed(() => props.multiple && props.useChipsWhenMultiple)

/** Resolve o título visível do item */
const itemTitle = (it: unknown): string => {
  const typed = it as Item | undefined
  const key = String(labelField.value)
  return typed && key in (typed as Record<string, unknown>)
    ? String((typed as Record<string, unknown>)[key])
    : String(it ?? '')
}

/** Resolve o valor do item */
const itemValue = (it: unknown): unknown => {
  const typed = it as Item | undefined
  const key = String(valueField.value)
  return typed && key in (typed as Record<string, unknown>)
    ? (typed as Record<string, unknown>)[key]
    : it
}

/** Debounce simples para 'search' */
let searchTimer: number | undefined
watch(
  () => searchText.value,
  (q) => {
    if (!searchable.value) return
    window.clearTimeout(searchTimer)
    searchTimer = window.setTimeout(() => {
      emit('search', q)
    }, props.searchDebounceMs)
  }
)

/** Atualiza model e propaga 'select' quando objeto(s) */
function onUpdate(val: unknown) {
  emit('update:modelValue', val as string | Item | Item[] | null)
  if (val && typeof val === 'object')
    emit('select', val as Item | Item[] | null)

  // fecha o menu em single-select quando configurado (sem usar any)
  if (props.closeOnSelect && !props.multiple) {
    menuOpen.value = false
  }
}

/** Submit via Enter */
function onEnter() {
  emit('submit')
}

/** Métodos públicos */
function focus() {
  comboboxRef.value?.focus()
}
function clear() {
  emit('update:modelValue', props.multiple ? ([] as Item[]) : null)
  searchText.value = ''
}

defineExpose({ focus, clear })

/** Preenche busca com label do valor atual ao montar (UX) */
onMounted(() => {
  if (!props.modelValue) return
  if (Array.isArray(props.modelValue)) {
    const first = props.modelValue[0]
    searchText.value = first ? itemTitle(first) : ''
  } else if (typeof props.modelValue === 'object') {
    searchText.value = itemTitle(props.modelValue)
  } else {
    searchText.value = String(props.modelValue ?? '')
  }
})
</script>

<template>
  <FieldControl
    :name="name"
    :label="label"
    :required="required"
    :hint="hint"
    :validate-on="validateOn"
    :error="error"
  >
    <!-- FieldControl expõe um único objeto { bind } -->
    <template #default="{ bind }">
      <InputShell>
        <VCombobox
          ref="comboboxRef"
          v-model:menu="menuOpen"
          v-model:search="searchText"
          class="w-100"
          v-bind="bind"
          :items="items ?? []"
          variant="outlined"
          :label="label"
          :placeholder="placeholder"
          :disabled="disabled"
          :loading="loading"
          :error="error"
          :multiple="multiple"
          :chips="chips"
          :clearable="clearable"
          :item-title="itemTitle"
          :item-value="itemValue"
          :model-value="modelValue"
          :return-object="allowCustom"
          attach="body"
          location-strategy="static"
          @update:model-value="onUpdate"
          @keydown.enter.prevent="onEnter"
        >
          <!-- Loader do campo (substitui o spinner padrão quando :loading="true") -->
          <template #loader>
            <v-progress-circular indeterminate size="16" />
          </template>

          <!-- Item no menu antes da lista (aparece só quando loading) -->
          <template #prepend-item>
            <div v-if="loading" class="text-caption px-3 py-2">
              {{ $t('general.loading') }}…
            </div>
          </template>

          <!-- Lupa -->
          <template #prepend-inner>
            <VIcon size="16" class="mr-1">
              <img
                :src="searchIcon"
                alt="search"
                style="height: 16px; width: 16px"
              />
            </VIcon>
          </template>

          <!-- Slot livre p/ itens adicionais -->
          <slot />

          <!-- Sem dados -->
          <template #no-data>
            <div class="text-body-2 text-grey-darken-1" style="padding: 8px">
              {{
                loading
                  ? $t('general.loading') + '…'
                  : searchText
                    ? $t('general.noResults')
                    : $t('general.startTyping')
              }}
            </div>
          </template>
        </VCombobox>

        <!-- Ações extras do shell (ex.: botão para abrir modal) -->
        <template #append>
          <slot name="append" />
        </template>
      </InputShell>
    </template>
  </FieldControl>
</template>

<style scoped>
/* Mantém o input “clean” */
:deep(.v-combobox .v-input__control) {
  padding: 0;
}
</style>
